import React, { useState, useMemo } from "react";
import { View, TextInput, StyleSheet, Platform } from "react-native";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import CustomDropdown, { CustomDropdownOption } from "../../../common/general/CustomDropDown/CustomDropdown";

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (value: string) => void;
}

export function FilterBar({ searchTerm, onSearchChange, severityFilter, onSeverityFilterChange }: FilterBarProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);

  const severityOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "All Severity", value: "all" },
      { label: "Critical", value: "critical" },
      { label: "High", value: "high" },
      { label: "Medium", value: "medium" },
      { label: "Low", value: "low" },
    ],
    []
  );

  return (
    <View style={styles.container}>
      {/* Search */}
      <View style={[styles.searchContainer, isSearchFocused && styles.searchContainerFocused]}>
        <MagnifyingGlassIcon
          size={16}
          color={isSearchFocused ? colors.blue[500] : colors.grey[400]}
          style={styles.searchIcon}
        />
        <TextInput
          style={[
            styles.searchInput,
            Platform.select({
              web: {
                outlineStyle: "none",
              } as unknown,
            }),
          ]}
          placeholder="Search by student, course, or content..."
          placeholderTextColor={colors.grey[500]}
          value={searchTerm}
          onChangeText={onSearchChange}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
        />
      </View>

      {/* Severity Filter */}
      <View style={[styles.filterWrapper, severityOpen && styles.filterWrapperOpen]}>
        <CustomDropdown
          options={severityOptions}
          value={severityFilter}
          onChange={onSeverityFilterChange}
          placeholder="All Severity"
          fullWidth
          accessibilityLabel="Filter by severity"
          open={severityOpen}
          onOpenChange={setSeverityOpen}
          dropdownStyle={styles.dropdownStyle}
          dropdownContainerStyle={styles.dropdownContainerStyle}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    gap: spacing.sm,
    position: "relative",
    zIndex: 100,
    overflow: "visible",
  },
  searchContainer: {
    flex: 2,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    minWidth: 200,
    height: spacing["2xl"],
  },
  searchContainerFocused: {
    borderColor: colors.blue[600],
    borderWidth: 2,
  },
  searchIcon: {
    marginRight: spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
  },
  filterWrapper: {
    flex: 1,
    minWidth: 100,
    position: "relative",
    zIndex: 1,
    overflow: "visible",
  },
  filterWrapperOpen: {
    zIndex: 5000,
    overflow: "visible",
  },
  dropdownStyle: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    height: spacing["2xl"],
    minHeight: spacing["2xl"],
  },
  dropdownContainerStyle: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
  },
});
